<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-md-3">
                <label>Idioma</label>
                <select class="form-control" v-model="filters.language_id" @change="updateList">
                  <option value="">Todos os idiomas</option>
                  <option v-for="lang in languagesList" :key="lang.id" :value="lang.id">
                    {{ lang.flag }} {{ lang.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label>Grupo</label>
                <select class="form-control" v-model="filters.group" @change="updateList">
                  <option value="">Todos os grupos</option>
                  <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
                </select>
              </div>
              <div class="col-md-4">
                <label>Buscar</label>
                <div class="search-box">
                  <div class="position-relative">
                    <input type="text" class="form-control" v-model="filters.search" placeholder="Buscar por chave ou valor..." @keyup.enter="doSearch"/>
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <button class="btn btn-primary w-100" @click="doSearch">
                  <i class="bx bx-search-alt"></i> Buscar
                </button>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="d-flex flex-wrap gap-2">
                  <button v-if="$can('admin.translations.add')" type="button" class="btn btn-success btn-rounded" @click="newTranslation()">
                    <i class="mdi mdi-plus mr-1"></i> Nova Tradução
                  </button>
                  <button v-if="$can('admin.translations.add')" type="button" class="btn btn-info btn-rounded" @click="showImportModal = true">
                    <i class="mdi mdi-upload mr-1"></i> Importar JSON
                  </button>
                  <button v-if="$can('admin.translations.list') && filters.language_id" type="button" class="btn btn-secondary btn-rounded" @click="exportTranslations()">
                    <i class="mdi mdi-download mr-1"></i> Exportar JSON
                  </button>
                  <button v-if="$can('admin.translations.add')" type="button" class="btn btn-warning btn-rounded" @click="showCopyModal = true">
                    <i class="mdi mdi-content-copy mr-1"></i> Copiar Traduções
                  </button>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <table class="table table-centered table-hover">
                <thead>
                  <tr>
                    <th width="15%">Idioma</th>
                    <th width="10%">Grupo</th>
                    <th width="25%">Chave</th>
                    <th width="40%">Valor</th>
                    <th width="10%">#</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in translations" :key="item.id">
                    <td>
                      <span v-if="item.language">{{ item.language.flag }} {{ item.language.code }}</span>
                    </td>
                    <td>
                      <span class="badge badge-soft-primary">{{ item.group }}</span>
                    </td>
                    <td>
                      <code>{{ item.key }}</code>
                    </td>
                    <td class="text-truncate" style="max-width: 300px;" :title="item.value">
                      {{ item.value }}
                    </td>
                    <td>
                      <button
                        v-if="$can('admin.translations.edit')"
                        class="btn btn-sm btn-outline-primary mr-1"
                        @click="editTranslation(item)"
                        title="Editar">
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        v-if="$can('admin.translations.delete')"
                        class="btn btn-sm btn-outline-danger"
                        @click="deleteTranslation(item.id)"
                        title="Excluir">
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="translations.length === 0">
                    <td colspan="5" class="text-center text-muted py-4">
                      Nenhuma tradução encontrada
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" @input="updateList"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Translation Form Modal -->
    <modal-form v-model="modalForm" :translation="selectedTranslation" :languages="languagesList" :groups="groups" ref="formTranslation" @created="updateList"></modal-form>

    <!-- Import Modal -->
    <b-modal v-model="showImportModal" title="Importar Traduções" size="md" :no-close-on-backdrop="true">
      <div class="form-group">
        <label>Idioma <span class="text-danger">*</span></label>
        <select class="form-control" v-model="importData.language_id">
          <option value="">Selecione um idioma</option>
          <option v-for="lang in languagesList" :key="lang.id" :value="lang.id">
            {{ lang.flag }} {{ lang.name }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label>Grupo <span class="text-danger">*</span></label>
        <input type="text" class="form-control" v-model="importData.group" placeholder="Ex: general, auth, errors">
      </div>
      <div class="form-group">
        <label>Arquivo JSON <span class="text-danger">*</span></label>
        <input type="file" class="form-control" accept=".json" @change="handleFileUpload">
      </div>
      <template v-slot:modal-footer>
        <b-button variant="secondary" @click="showImportModal = false">Cancelar</b-button>
        <b-button variant="primary" @click="doImport" :disabled="!importData.language_id || !importData.group || !importData.file">
          Importar
        </b-button>
      </template>
    </b-modal>

    <!-- Copy Modal -->
    <b-modal v-model="showCopyModal" title="Copiar Traduções" size="md" :no-close-on-backdrop="true">
      <div class="form-group">
        <label>Idioma de Origem <span class="text-danger">*</span></label>
        <select class="form-control" v-model="copyData.source_language_id">
          <option value="">Selecione o idioma de origem</option>
          <option v-for="lang in languagesList" :key="lang.id" :value="lang.id">
            {{ lang.flag }} {{ lang.name }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label>Idioma de Destino <span class="text-danger">*</span></label>
        <select class="form-control" v-model="copyData.target_language_id">
          <option value="">Selecione o idioma de destino</option>
          <option v-for="lang in languagesList" :key="lang.id" :value="lang.id" :disabled="lang.id === copyData.source_language_id">
            {{ lang.flag }} {{ lang.name }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label>Grupo (opcional)</label>
        <select class="form-control" v-model="copyData.group">
          <option value="">Todos os grupos</option>
          <option v-for="g in groups" :key="g" :value="g">{{ g }}</option>
        </select>
      </div>
      <div class="form-check">
        <input type="checkbox" class="form-check-input" id="overwrite" v-model="copyData.overwrite">
        <label class="form-check-label" for="overwrite">Sobrescrever traduções existentes</label>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="secondary" @click="showCopyModal = false">Cancelar</b-button>
        <b-button variant="primary" @click="doCopy" :disabled="!copyData.source_language_id || !copyData.target_language_id">
          Copiar
        </b-button>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import ModalForm from './Form'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import permissionMixin from '@/mixins/permissionMixin'
import languageService from '@/pages/languages/services'

export default {
  mixins: [permissionMixin],
  components: {
    Layout,
    PageHeader,
    ModalForm
  },
  name: 'translations',
  page: {
    title: 'Traduções',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      loading: true,
      modalForm: false,
      showImportModal: false,
      showCopyModal: false,
      filters: {
        language_id: '',
        group: '',
        search: ''
      },
      params: {},
      totalRows: 1,
      currentPage: 1,
      perPage: 50,
      selectedTranslation: null,
      languagesList: [],
      importData: {
        language_id: '',
        group: '',
        file: null
      },
      copyData: {
        source_language_id: '',
        target_language_id: '',
        group: '',
        overwrite: false
      },
      title: 'Gerenciamento de Traduções',
      items: [{
        text: 'Administração'
      },
      {
        text: 'Traduções',
        active: true
      }]
    }
  },
  async mounted () {
    await this.loadLanguages()
    await this.loadGroups()

    // Check if came from languages page with language_id
    if (this.$route.query.language_id) {
      this.filters.language_id = parseInt(this.$route.query.language_id)
    }

    this.updateList()
    this.loading = false
  },
  computed: {
    ...mapState('translation', ['translations', 'pagination', 'groups']),
    rows () {
      return this.pagination.total || 0
    }
  },
  methods: {
    ...mapActions('translation', {
      getAll: 'getAll',
      getGroups: 'getGroups',
      deleteItem: 'deleteTranslation',
      importAction: 'importTranslations',
      exportAction: 'exportTranslations',
      copyAction: 'copyTranslations'
    }),
    async loadLanguages () {
      try {
        const res = await languageService.list({ limit: 100 })
        this.languagesList = res.data.data
      } catch (error) {
        console.error('Error loading languages:', error)
      }
    },
    async loadGroups () {
      try {
        await this.getGroups()
      } catch (error) {
        console.error('Error loading groups:', error)
      }
    },
    newTranslation () {
      this.selectedTranslation = null
      this.$refs.formTranslation.open()
      this.modalForm = true
    },
    editTranslation (item) {
      this.selectedTranslation = { ...item }
      this.$refs.formTranslation.open(item)
      this.modalForm = true
    },
    deleteTranslation (id) {
      Swal.fire({
        title: 'Você tem certeza?',
        text: 'Essa ação não poderá ser revertida.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, EXCLUIR!'
      }).then(result => {
        if (result.value) {
          this.deleteItem(id).then(() => {
            successMessage('Tradução excluída com sucesso.', 2500)
            this.updateList()
          }).catch((err) => {
            errorMessage('Erro: ' + err.message)
          })
        }
      })
    },
    handleFileUpload (event) {
      this.importData.file = event.target.files[0]
    },
    async doImport () {
      if (!this.importData.language_id || !this.importData.group || !this.importData.file) {
        errorMessage('Preencha todos os campos obrigatórios.')
        return
      }

      let loader = this.$loading.show()
      try {
        await this.importAction(this.importData)
        successMessage('Traduções importadas com sucesso!', 2500)
        this.showImportModal = false
        this.importData = { language_id: '', group: '', file: null }
        this.loadGroups()
        this.updateList()
      } catch (err) {
        errorMessage('Erro ao importar: ' + err.message)
      } finally {
        loader.hide()
      }
    },
    async exportTranslations () {
      if (!this.filters.language_id) {
        errorMessage('Selecione um idioma para exportar.')
        return
      }

      let loader = this.$loading.show()
      try {
        const res = await this.exportAction({
          languageId: this.filters.language_id,
          group: this.filters.group
        })

        // Download the JSON file
        const dataStr = JSON.stringify(res.data, null, 2)
        const dataBlob = new Blob([dataStr], { type: 'application/json' })
        const url = URL.createObjectURL(dataBlob)
        const link = document.createElement('a')
        link.href = url
        const lang = this.languagesList.find(l => l.id === this.filters.language_id)
        const filename = this.filters.group
          ? `${lang.code}_${this.filters.group}.json`
          : `${lang.code}.json`
        link.download = filename
        link.click()
        URL.revokeObjectURL(url)

        successMessage('Traduções exportadas com sucesso!', 2500)
      } catch (err) {
        errorMessage('Erro ao exportar: ' + err.message)
      } finally {
        loader.hide()
      }
    },
    async doCopy () {
      if (!this.copyData.source_language_id || !this.copyData.target_language_id) {
        errorMessage('Selecione os idiomas de origem e destino.')
        return
      }

      let loader = this.$loading.show()
      try {
        const res = await this.copyAction(this.copyData)
        successMessage(res.data.message || 'Traduções copiadas com sucesso!', 2500)
        this.showCopyModal = false
        this.copyData = { source_language_id: '', target_language_id: '', group: '', overwrite: false }
        this.updateList()
      } catch (err) {
        errorMessage('Erro ao copiar: ' + err.message)
      } finally {
        loader.hide()
      }
    },
    getReport () {
      let params = {
        search: this.filters.search || null,
        language_id: this.filters.language_id || null,
        group: this.filters.group || null,
        limit: this.perPage,
        page: this.currentPage
      }
      this.params = params
      let loader = this.$loading.show()
      this.getAll(this.params).then(() => {
        this.totalRows = this.pagination.total
        this.currentPage = this.pagination.current_page
        loader.hide()
      }).catch(error => {
        console.log(error)
        loader.hide()
      })
    },
    doSearch () {
      this.currentPage = 1
      this.getReport()
    },
    updateList () {
      this.getReport()
    }
  }
}
</script>
<style scoped>
.link { cursor: pointer; }
.swal2-popup {
  font-size: 11px !important;
}
.table-responsive { min-height: 300px; }
.float-sm-end { float: right }
.gap-2 {
  gap: 0.5rem;
}
.badge-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
}
</style>
